// vee-validate setup for globally useful validators
import VeeValidate from "vee-validate";

/**
 * Creates a validator function which uses a regex checking for the character class
 * provided.
 *
 * @param {string} char_class
 *
 * @returns {(val: string) => boolean}
 */
export const contains_char_class = (char_class) => {
  const usable = typeof char_class === `string` ? char_class : char_class.source;
  // Word to the wise: the 'g' flag stores state about the position the regex reached in its last
  // run, and when run again, will begin from that position. Don't use it for cases like this!
  const regexp = new RegExp(`^.*(?:[${usable}]).*$`);
  return val => regexp.test(val);
};

export const contains_lc_alpha = contains_char_class(`a-z`); // contains a-z
export const contains_uc_alpha = contains_char_class(`A-Z`); // contains A-Z
export const contains_digit = contains_char_class(`\\d`); // contains 0-9
export const contains_special = contains_char_class(`-+=_:;,.><¬#~|'"£%^@$!%*?&()\\[\\]{}\\/`); // contains anything here

export const has_file_extension = (value) => {
  const regexp = new RegExp(`/(.+)\\.(\\w+)/g`);
  if (value) {
    return regexp.test(value);
  }
  return false;
};

VeeValidate.Validator.extend(`without_file_extension`, {
  getMessage: () => `Must not include the file extension`,
  validate: value =>
    new Promise((resolve) => {
      resolve({
        valid: !has_file_extension(value),
      });
    }),
});

VeeValidate.Validator.extend(`with_file_extension`, {
  getMessage: () => `Must not include the file extension`,
  validate: value =>
    new Promise((resolve) => {
      resolve({
        valid: has_file_extension(value),
      });
    }),
});

VeeValidate.Validator.extend(`has_lc_alpha`, {
  getMessage: () => `Must contain at least 1 lowercase character ( [a-z] )`,
  validate: contains_lc_alpha,
});
VeeValidate.Validator.extend(`has_uc_alpha`, {
  getMessage: () => `Must contain at least 1 uppercase character ( [A-Z] )`,
  validate: contains_uc_alpha,
});

VeeValidate.Validator.extend(`has_digit`, {
  getMessage: () => `Must contain at least 1 digit ( [0-9] )`,
  validate: contains_digit,
});
VeeValidate.Validator.extend(`has_special`, {
  getMessage: () => `Must contain at least 1 special character`,
  validate: contains_special,
});

VeeValidate.Validator.extend("is_time", {
  getMessage: () => `The format must be HH:MM:SS`,
  validate: value =>
    new Promise((resolve) => {
      const regex = new RegExp(
        "([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])",
      );
      resolve({
        valid: value && regex.test(value),
      });
    }),
});

VeeValidate.Validator.extend("alpha_underscores", {
  getMessage: () =>
    `Slugs can only contain letters, numbers and underscores`,
  validate: (value) => {
    const alpha_underscores = new RegExp("^[A-Za-z0-9_]*$");
    return alpha_underscores.test(value);
  },
});

VeeValidate.Validator.extend("alpha_num_spaces", {
  getMessage: () =>
    `Customer names can only include letters, spaces and numbers`,
  validate: (value) => {
    const alpha_num_spaces = new RegExp("^[A-Za-z0-9 ]*$");
    return alpha_num_spaces.test(value);
  },
});

VeeValidate.Validator.extend("episode_publish_start_before_publish_stop", {
  getMessage() {
    return 'Publish Start must be after Publish Stop';
  },
  validate(value, args) {
    const publish_start = moment(value, 'YYYY-MM-DD HH:mm:ss').unix();
    const publish_stop = moment(args[0]).unix();

    if (!publish_stop) {
      return true;
    }

    return publish_start <= publish_stop;
  },
});

VeeValidate.Validator.extend("episode_publish_stop_after_publish_start", {
  getMessage() {
    return 'Publish Stop must be after Publish Start';
  },
  validate(value, args) {
    const publish_start = moment(args[0]).unix();
    const publish_stop = moment(value, 'YYYY-MM-DD HH:mm:ss').unix();

    if (!publish_start) {
      return true;
    }

    return publish_stop >= publish_start;
  },
});

VeeValidate.config.fieldsBagName = "veeFields";

// Workaround issue with conflicting error props name with errorBagName (for vue-formulate)
// There is no way to set it for vue-formulate and vee-validate has issues with setting bag name on page with vue-formulate
if (window.isVueFormulate) {
  VeeValidate.config.errorBagName = 'veeErrors';
}

// eslint-disable-next-line snakecasejs/snakecasejs
export const ConfiguredVeeValidator = VeeValidate;
